import { useCallback } from "react";
import * as Sentry from "@sentry/browser";
import { useToken } from "@bn/api";
import {
  readStoredToken,
  revokeWealthApiTokens,
  USE_INTERNAL_AUTH,
} from "@helpers";

import { useMandatorLinks } from "./useMandatorLinks";

const PORTAL_LOGOUT_TIMEOUT_MS = 3000;

// "no-cors": the portal's CORS answer may not allow credentials (ariva prod
// sends `*`), and only the side effect matters, not the response.
const endPortalSession = async (url: string) => {
  try {
    await fetch(url, {
      method: "POST",
      mode: "no-cors",
      credentials: "include",
      keepalive: true,
    });
  } catch (error) {
    Sentry.captureException(error);
  }
};

export const useLogout = () => {
  const { logout: tokenLogout } = useToken({ enabled: false });
  const { logoutRedirectUrl, logoutApiUrl, revokeTokensOnLogout } =
    useMandatorLinks();

  const logout = useCallback(async () => {
    if (USE_INTERNAL_AUTH) {
      tokenLogout();
      window.location.href = import.meta.env.VITE_APP_ROOT_URL!;
      return;
    }

    const token = revokeTokensOnLogout ? readStoredToken() : undefined;
    tokenLogout();

    await Promise.race([
      Promise.all([
        token && revokeWealthApiTokens(token),
        logoutApiUrl && endPortalSession(logoutApiUrl),
      ]),
      new Promise((resolve) => setTimeout(resolve, PORTAL_LOGOUT_TIMEOUT_MS)),
    ]);
    window.location.href =
      logoutRedirectUrl ?? import.meta.env.VITE_APP_ROOT_URL!;
  }, [tokenLogout, logoutRedirectUrl, logoutApiUrl, revokeTokensOnLogout]);

  return {
    logout,
  };
};
